import React, { CSSProperties, FC, useEffect, useMemo } from 'react'
import './CertificatePaymentPreviewCertificateInformation.scss'
import CertificatePaymentPreviewWrapper from '../CertificatePaymentPreviewWrapper/CertificatePaymentPreviewWrapper'
import FormTextField from 'app_components/commons/FormTextField/FormTextField';
import { Control, Controller, DeepMap, FieldError, FieldValues } from 'react-hook-form';
import LabelComponent from 'app_components/commons/LabelComponent/LabelComponent';
import { CiWarning } from "react-icons/ci";
import TextComponent from 'app_components/commons/TextComponent/TextComponent';
import { IoIosCloseCircleOutline, IoMdRadioButtonOff, IoMdRadioButtonOn } from 'react-icons/io';
import { customs } from 'app_config/globals';
import { ICertificatePaymentPreview } from 'app_types/user/certificateTypes';
import FormSelect from 'app_components/commons/FormSelect/FormSelect';
import { FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup } from '@material-ui/core';
import { ERPClassification, ERPNature } from 'app_types/erp/erpTypes';
import ReactSelectCommon from 'app_components/commons/reactSelect';
import { ERP_COUNTRIES } from 'app_types/erp/erpCountries';
import { ERP_DEPARTMENTS } from 'app_types/erp/erpDepartments';
import { ERP_CITIES } from 'app_types/erp/erpCities';
import { EfipayCurrency } from 'app_types/efipay/efipayTypes';

const labelStyles: CSSProperties = {
  color: '#545C61',
  fontWeight: 300,
}

interface ICertificatePaymentPreviewCertificateInformationProps {
  onClose: () => void
  progress: string
  onNext: () => void
  errors: DeepMap<FieldValues, FieldError>
  control: Control<FieldValues>
  handleSubmit: unknown
  price: Record<EfipayCurrency, number>
  data: ICertificatePaymentPreview
  isLoading: boolean
  currentNature: ERPNature
  formHook: any
}

const CertificatePaymentPreviewCertificateInformation: FC<ICertificatePaymentPreviewCertificateInformationProps> = ({
  progress,
  onClose,
  onNext,
  errors,
  control,
  handleSubmit,
  price: priceData,
  data,
  isLoading,
  currentNature,
  formHook,
}) => {

  const {certificate: {title}} = data

  useEffect(() => {
    if (currentNature === ERPNature.NATURAL) {
      control.setValue('classification', ERPClassification.NO_RESPONSABLE)
    } else {
      control.setValue('classification', ERPClassification.ENTIDADES_DEL_ESTADO)
    }
  }, [currentNature])

  const country = formHook?.watch('country')
  const department = formHook?.watch('department')
  const currency: EfipayCurrency = formHook?.watch('currency') ? formHook?.watch('currency') : EfipayCurrency.COP

  const price = priceData[currency]
  const iva = (price * 0.19)
  const valorTotal= (iva + price)

  const COPformat = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: currency ? currency : 'COP',
    minimumFractionDigits: 0
  })

  const formatoPesosColombianosIva = COPformat.format(iva);
  const priceCOPFormatted = COPformat.format(price)

  const formatoPesosColombianosValorTotal = COPformat.format(valorTotal);

  const countriesToRender = useMemo(() => {
    return [...ERP_COUNTRIES]
  }, [])
  const departmentsToRender = useMemo(() => {
    return country?.label ? [...ERP_DEPARTMENTS?.filter(({ country: countryItem }) => country?.label === countryItem)] : []
  }, [country])
  const citiesToRender = useMemo(() => {
    return department?.value ? [...ERP_CITIES?.filter(({ department: departmentItem }) => departmentItem === department.value)] : []
  }, [department])

  return (
    <CertificatePaymentPreviewWrapper
      title='Resumen de pago'
      progress={progress}
      cancelButtonText='Cancelar'
      confirmButtonText='Pagar'
      onCancel={onClose}
      onConfirm={onNext}
      handleSubmit={handleSubmit}
      hideProgress
      isLoading={isLoading}
    >
      <>
        <IoIosCloseCircleOutline onClick={onClose} color='var(--secondary)'className='certificate-payment-preview-certificate-information--icon'/>
        <div>
          <div>
            <p className='certificate-payment-preview-certificate-information--subtitle'>Descripcion: Pago de {title}</p>
            <p className='certificate-payment-preview-certificate-information--subtitle-cost'>Costo del certificado: {priceCOPFormatted}</p>
            <p className='certificate-payment-preview-certificate-information--subtitle-cost'>Porcentaje de IVA: {formatoPesosColombianosIva}</p>
            <p className='certificate-payment-preview-certificate-information--total-value'><strong>Valor total:</strong>  {formatoPesosColombianosValorTotal}</p>
            <Controller
              control={control}
              rules={{ required: true }}
              name='currency'
              render={({ onChange, value }) => (
                <FormControl component="fieldset">
                  <FormLabel component="legend" style={{ color: 'rgb(84, 92, 97)' }}>Moneda</FormLabel>
                  <RadioGroup
                    aria-label="currency"
                    name="currency"
                    value={value}
                    onChange={onChange}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '5px'
                    }}
                  >
                    {Object.keys(EfipayCurrency)
                          .filter((key) => EfipayCurrency[key as keyof typeof EfipayCurrency] === "COP")
                          .map((key) => (
                      <FormControlLabel
                        key={key}
                        value={EfipayCurrency[key as keyof typeof EfipayCurrency]}
                        control={<Radio
                          icon={<IoMdRadioButtonOff />}
                          checkedIcon={<IoMdRadioButtonOn color='var(--primary)' />}
                        />}
                        label={EfipayCurrency[key as EfipayCurrency]}
                        checked={value === EfipayCurrency[key as keyof typeof EfipayCurrency]}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            >
            </Controller>
          </div>
          <TextComponent
            color='#545C61'
            fontWeight={700}
            fontSize='100%'
            width='100%'
            display='flex'
            justifyContent='center'
          >
            Verifica tus datos
          </TextComponent>
          <div className='certificate-payment-preview-certificate-information--warning'>
            <CiWarning color='#C89000' size={16}/>
            <LabelComponent color='#545C61' fontWeight={300} fontSize='75%'>Ten en cuenta que esta información es la que se va a visualizar en tu certificado</LabelComponent>
          </div>

          <FormTextField
            control={control}
            id='fullName'
            error={errors.fullName}
            label="Nombre en el certificado"
            required
          />
          <LabelComponent color='#545C61' fontWeight={300} fontSize='75%' marginBottom='12px'marginTop='4' marginLeft='16px'>Nombre que aparecerá en el certificado</LabelComponent>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
            <div>
              <LabelComponent style={labelStyles}>País</LabelComponent>
              <ReactSelectCommon
                isMulti={false}
                options={countriesToRender}
                formHooks={formHook}
                identifier='country'
                required
              />
              {formHook?.errors?.country && (
                <LabelComponent>Este campo es obligatorio</LabelComponent>
              )}
            </div>
            <div>
              <LabelComponent style={labelStyles}>Departamento</LabelComponent>
              <ReactSelectCommon
                isMulti={false}
                options={departmentsToRender}
                formHooks={formHook}
                identifier='department'
                required
              />
              {formHook?.errors?.department && (
                <LabelComponent>Este campo es obligatorio</LabelComponent>
              )}
            </div>
            <div>
              <LabelComponent style={labelStyles}>Ciudad</LabelComponent>
              <ReactSelectCommon
                isMulti={false}
                options={citiesToRender}
                formHooks={formHook}
                identifier='city'
                required
              />
              {formHook?.errors?.city && (
                <LabelComponent>Este campo es obligatorio</LabelComponent>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', margin: '20px 0px' }}>
            <FormSelect
              control={control}
              id='nature'
              label='Naturaleza'
              variant='outlined'
              error={!!errors?.nature}
              required
            >
              <MenuItem value={ERPNature.NATURAL}>Persona Natural</MenuItem>
              <MenuItem value={ERPNature.JURIDICA}>Persona Jurídica</MenuItem>
            </FormSelect>
            {currentNature === ERPNature.JURIDICA && (
              <FormSelect
                control={control}
                id='classification'
                label='Clasificación'
                variant='outlined'
                error={!!errors?.classification}
                required
              >
                {Object.values(ERPClassification)
                  .filter((value) => value !== ERPClassification.NO_RESPONSABLE)
                  .map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))
                }
              </FormSelect>
            )}
          </div>
          <FormTextField
            control={control}
            id='docNumber'
            error={errors.docNumber}
            label="Documento en el certificado"
            required
            disabled
            inputProps={{
              style: { color: '#bbbdc0'}
            }}
          />
          <LabelComponent color='#bbbdc0' fontWeight={400} fontSize='75%' margin='6px' marginLeft='16px'>Confirme su numero de documento</LabelComponent>
        </div>
        <LabelComponent
          className='certificate-payment-preview-certificate-information--help'
        >
          ¿Tienes problemas con tus datos personales? &nbsp;<a href={customs.helpdesk} target="_blank" className='certificate-payment-preview-certificate-information--help-two'>Ir a Mesa de ayuda</a>
        </LabelComponent>
      </>
    </CertificatePaymentPreviewWrapper>
  )
}

export default CertificatePaymentPreviewCertificateInformation
